import { useAllParagraphs } from "./useAllParagraphs";

// Common abbreviations for sentence detection

const COMMON_ABBREVS = new Set([
  // Titles / Honorifics
  "mr.", "mrs.", "ms.", "dr.", "prof.", "sr.", "jr.",
  "rev.", "rep.", "sen.", "gov.", "gen.", "col.", "maj.", "capt.", "lt.", "sgt.",
  "supt.", "pres.", "atty.", "hon.",
  
  // Academic / Professional
  "ph.d.", "m.d.", "b.a.", "m.a.", "d.d.s.", "esq.",
  
  // Geography / Address
  "st.", "rd.", "ave.", "blvd.", "pl.", "sq.", "ln.", "dr.", // (drive)
  "mt.", "ft.", "u.s.", "u.k.", "d.c.", "n.y.", "l.a.",
  
  // Business / Legal
  "co.", "inc.", "ltd.", "llc.", "dept.", "corp.", "est.", "approx.",
  "vs.", "v.",
  
  // Latin / Common
  "etc.", "e.g.", "i.e.", "cf.", "al.", "et al.", "viz.", "ibid.", "vol.", "no.", "p.", "pp.", "fig.",
  
  // Time (already have a.m./p.m.)
  "a.m.", "p.m.",
  
  // Months (if used as abbr)
  "jan.", "feb.", "mar.", "apr.", "jun.", "jul.", "aug.", "sep.", "sept.", "oct.", "nov.", "dec."
]);

export type SentenceRange = {
  start: number;
  end: number;
  length: number;
  sentenceIndex: number;
  text: string;
  isItalic?: boolean;
};

const SENTENCE_OPENER_MARKER_REGEX = /^[\[\(\{]\d+[\]\)\}]/;
const OPENING_QUOTE_CHARS = new Set(['"', "'", "“", "‘"]);

export const useSentenceOpenerStart = (sentence: SentenceRange) => {
  let offset = 0;

  while (
    offset < sentence.text.length &&
    /[\s\t\u200B]/.test(sentence.text[offset])
  ) {
    offset++;
  }

  const markerMatch = sentence.text
    .slice(offset)
    .match(SENTENCE_OPENER_MARKER_REGEX);

  if (markerMatch) {
    offset += markerMatch[0].length;

    while (
      offset < sentence.text.length &&
      /[\s\t\u200B]/.test(sentence.text[offset])
    ) {
      offset++;
    }
  }

  while (
    offset < sentence.text.length &&
    OPENING_QUOTE_CHARS.has(sentence.text[offset])
  ) {
    offset++;

    while (
      offset < sentence.text.length &&
      /[\s\t\u200B]/.test(sentence.text[offset])
    ) {
      offset++;
    }
  }

  return sentence.start + offset;
};

export const useParagraphSentences = (
  quillDelta: any[],
  quill: any,
  paragraphIndex: number,
  options?: { checkItalic?: boolean }
): SentenceRange[] => {
  const allParagraphs = useAllParagraphs(quill);
  const paragraph = allParagraphs[paragraphIndex];

  if (!paragraph) {
    return [];
  }

  // Use Quill's API to get accurate index and text
  // We use quill.getIndex() because useAllParagraphs filters paragraphs, 
  // so paragraph.start might not match the actual document index
  const paragraphStart = quill.getIndex(paragraph);
  // block.length() usually includes the newline
  const paragraphLength = paragraph.length(); 
  const paragraphText = quill.getText(paragraphStart, paragraphLength);

  // If paragraph is empty or just whitespace, return empty array
  if (!paragraphText.trim()) {
    return [];
  }

  // Find all sentences in the paragraph
  const sentences: SentenceRange[] = [];
  const ender = /([.!?…]+)([)"'\]\u2019\u201D]*)/g;

  // Recognize a whole initials sequence, including its first period: C.J.,
  // C. J., J.R.R., and C.J without the final period. Keep original offsets.
  const initialPeriods = new Set<number>();
  const initials = /(?:^|[^\p{L}\p{N}_])((?:\p{Lu}\.[ \t]*)+\p{Lu}\.?)(?=$|[^\p{L}\p{N}_])/gu;
  for (const match of paragraphText.matchAll(initials)) {
    const start = match.index! + match[0].length - match[1].length;
    for (let offset = 0; offset < match[1].length; offset++) {
      if (match[1][offset] === ".") initialPeriods.add(start + offset);
    }
  }

  // Helper function to check if a boundary is "false" (abbreviation, decimal, initial)
  function isFalseBoundary(idx: number, punctuation: string, matchLen: number): boolean {
    // Initials and abbreviations only protect single periods, never !, ? or ... .
    if (punctuation !== ".") return false;
    if (initialPeriods.has(idx)) return true;
    const end = idx + matchLen;
    const markStart = idx;
    const dotRun = /^[.]+$/.test(paragraphText.slice(markStart, markStart + 1));

    // 1) Decimal like "3.14"
    if (dotRun) {
      const prev = paragraphText[markStart - 1];
      const next = paragraphText[end] ?? paragraphText[markStart + 1];
      if (/\d/.test(prev) && /\d/.test(next)) return true;
    }

    // Capture the text immediately preceding the current punctuation
    const textBefore = paragraphText.slice(0, markStart);

    // 2) Initials / Middle Names like "George W. Bush" or "John F. Kennedy"
    // Check if the word before the dot is a single uppercase letter.
    // We exclude 'I' (common word) unless it appears to be part of a name flow.
    // We also check that it's not the end of the paragraph.
    if (dotRun) {
      const initialMatch = /(?:^|[\s"'“‘([{])([A-Z])\s*$/.exec(textBefore);
      if (initialMatch) {
        const letter = initialMatch[1];
        if (letter !== 'I') {
          return true;
        }
      }
    }

    // 4) Common abbreviations (enhanced for a.m. / p.m.)
    // Allow dots in the word match to catch multi-part abbreviations at the end
    const wordMatch = /([A-Za-z.]{1,10})\s*$/.exec(textBefore);
    
    if (wordMatch && dotRun) {
      let abbr = (wordMatch[1] + ".").toLowerCase();
      
      // Handle the FIRST dot of a.m. / p.m. (where we are at "a." or "p.")
      // We look ahead to see if 'm.' follows.
      if ((abbr === 'a.' || abbr === 'p.') && /^m\./i.test(paragraphText.slice(end))) {
        return true;
      }

      // Handle the SECOND dot of a.m. / p.m.
      // Because we allowed dots in `wordMatch` regex above, `abbr` might now be "a.m." or "p.m."
      if (COMMON_ABBREVS.has(abbr)) return true;
      
      // Fallback for cases where the regex might have split differently
      // e.g. if wordMatch captured just "m", check if previous char was "." and char before was "a" or "p"
      if (abbr === 'm.') {
         const preM = textBefore.trim().slice(-3); // look for "a.m" or "p.m" pattern ending
         if (/a\.m$/i.test(preM) || /p\.m$/i.test(preM)) return true;
      }
    }

    return false;
  }

  // Find all sentence boundaries
  const boundaries: number[] = [0]; // Start of paragraph is always a boundary
  let m: RegExpExecArray | null;

  while ((m = ender.exec(paragraphText)) !== null) {
    const fullLen = m[0].length;
    let boundary = m.index + fullLen;

    if (!isFalseBoundary(m.index, m[1], fullLen)) {
      // Check for (voc) suffix
      const vocMatch = /^\s*\(voc\)/i.exec(paragraphText.slice(boundary));
      if (vocMatch) {
        boundary += vocMatch[0].length;
        ender.lastIndex = boundary;
      }

      // Skip whitespace after the punctuation to get to the start of the next sentence
      let nextSentenceStart = boundary;
      while (
        nextSentenceStart < paragraphText.length &&
        /\s/.test(paragraphText[nextSentenceStart])
      ) {
        nextSentenceStart++;
      }

      // Add this as a boundary if there's more text after it
      if (nextSentenceStart < paragraphText.length) {
        boundaries.push(nextSentenceStart);
      }
    }
  }

  // Extract sentences from boundaries
  for (let i = 0; i < boundaries.length; i++) {
    const sentenceStart = boundaries[i];
    const sentenceEnd =
      i < boundaries.length - 1 ? boundaries[i + 1] : paragraphText.length;

    // Trim leading whitespace
    let trimmedStart = sentenceStart;
    while (
      trimmedStart < sentenceEnd &&
      /[\s\t\u200B]/.test(paragraphText[trimmedStart])
    ) {
      trimmedStart++;
    }

    // Trim trailing whitespace (but not newlines at the very end of paragraph)
    let trimmedEnd = sentenceEnd;
    while (
      trimmedEnd > trimmedStart &&
      /[\s\t\u200B]/.test(paragraphText[trimmedEnd - 1]) &&
      paragraphText[trimmedEnd - 1] !== "\n"
    ) {
      trimmedEnd--;
    }

    // Skip empty sentences
    if (trimmedStart >= trimmedEnd) {
      continue;
    }

    const sentenceText = paragraphText.slice(trimmedStart, trimmedEnd);

    const sentenceRange: SentenceRange = {
      start: paragraphStart + trimmedStart,
      end: paragraphStart + trimmedEnd,
      length: trimmedEnd - trimmedStart,
      sentenceIndex: sentences.length,
      text: sentenceText,
    };

    if (options?.checkItalic) {
      const contents = quill.getContents(
        sentenceRange.start,
        sentenceRange.length
      );
      let totalLen = 0;
      let italicLen = 0;

      contents.ops.forEach((op: any) => {
        const len = typeof op.insert === "string" ? op.insert.length : 1;
        totalLen += len;
        if (op.attributes && op.attributes.italic) {
          italicLen += len;
        }
      });

      sentenceRange.isItalic = totalLen > 0 && italicLen / totalLen >= 0.75;
    }

    sentences.push(sentenceRange);
  }

  return sentences;
};
